import { Benefits } from '../benefits';
import { Faq } from '../faq';
import { HowItWorks } from '../how-it-works';
import { H2, P1, Skeleton } from '@zooplus/ui-components';
import { useTranslation } from '../../hooks/use-translations';
import { Reviews } from '../reviews';
import React, { useEffect, useState } from 'react';
import styles from './CentralHub.module.scss';
import { MostSubscribed } from '../most-subscribed/MostSubscribed';
import { EligibleBrands } from '../eligible-brands';
import { Examples } from '../examples';
import { DidYouKnow } from '../did-you-know/DidYouKnow';
import useToken from '../../hooks/useToken';
import { PreviouslyOrdered } from '../previously-ordered/PreviouslyOrdered';
import { CentralHubService } from '../../services/CentralHubService';
import { useSiteContext } from '@zooplus/commons-lang';
import { LastEligibleOrderInfo } from '../../model/order';
import { PdpArticle } from '../pdp-article/PdpArticle';
import { ProductInfo } from '../../model/product';
import { UpcomingSubscriptions } from '../upcoming-subscriptions';
import { SubscriptionInfo } from '../../model/subscriptionProducts';
import { ContentBanner } from '../common/content-banner/ContentBanner';
import {Brand} from "../../model/site";

export const CentralHub = () => {
  const { t } = useTranslation();
  const { token } = useToken();
  const { site, language } = useSiteContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingSiteInfo, setLoadingSiteInfo] = useState<boolean>(true);
  const [autoshipmentInfo, setAutoshipmentInfo] = useState<SubscriptionInfo | undefined>(undefined);
  const [pdpInfo, setPdpInfo] = useState<ProductInfo | undefined>(undefined);
  const [lastEligibleOrder, setLastEligibleOrder] = useState<LastEligibleOrderInfo | undefined>(
    undefined,
  );
  const [brands, setBrands] = useState<Brand[] | undefined>(undefined);
  const [firstOrderDiscount, setFirstOrderDiscount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (token && !lastEligibleOrder) {
        CentralHubService(token)
            .getLastEligibleOrder(site.domain)
            .then((response) => {
                if (response.url) {
                    setLastEligibleOrder(response);
                }
            });
    }
  }, [token]);

  useEffect(() => {
    const shopIdentifier = new URLSearchParams(location.search).get('activeVariant');
    if (shopIdentifier)
      CentralHubService(undefined)
        .getPdpArticleInformation(shopIdentifier, language)
        .then((response) => {
          if (response.articleShopId) {
            setPdpInfo(response);
          }
        });

  }, []);

  useEffect(() => {
    CentralHubService(undefined)
        .getSiteInfo(site.domain)
        .then((response) => {
            if (response) {
                setBrands(response.brands);
                setFirstOrderDiscount(response.firstOrderDiscount)
            }
        })
        .finally(() => setLoadingSiteInfo(false));
  }, []);

  useEffect(() => {
    CentralHubService(token)
      .getSubscriptions(site.domain)
      .then((response) => {
        if (response.upcomingAutoshipments) {
          setAutoshipmentInfo(response);
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const comboSection = () => {
    if (!pdpInfo && !lastEligibleOrder) return <Benefits firstOrderDiscount={firstOrderDiscount} />;
    else if (lastEligibleOrder && pdpInfo) {
      return (
        <>
          <Benefits firstOrderDiscount={firstOrderDiscount} />
          <div className={styles.comboSection}>
            <PdpArticle articleInfo={pdpInfo} />
            <PreviouslyOrdered lastEligibleOrderInfo={lastEligibleOrder} />
          </div>
        </>
      );
    } else
      return (
        <>
          <div className={styles.comboSection}>
            <Benefits benefitsComboStyles={true} firstOrderDiscount={firstOrderDiscount} />
            {lastEligibleOrder ? (
              <PreviouslyOrdered lastEligibleOrderInfo={lastEligibleOrder} />
            ) : (
              <PdpArticle articleInfo={pdpInfo!} />
            )}
          </div>
        </>
      );
  };

  const centralHubTitle = (
    <section>
      <H2>{t('title')}</H2>
      <P1 bold className={styles.sentence}>
        {t('description')}
      </P1>
    </section>
  );

  const campaignTextContainer = 'campaign-info-text';

  const content = () => {
    if (loading || loadingSiteInfo) return <Skeleton className={styles.skeleton} height={'500'} />;
    return autoshipmentInfo?.upcomingAutoshipments ? (
      <>
        <UpcomingSubscriptions subscriptions={autoshipmentInfo} />
        <MostSubscribed />
        <DidYouKnow />
        <Faq />
        <EligibleBrands brands={brands || []} />
      </>
    ) : (
      <>
        <ContentBanner productGroup={33333} zone={'M2'} textContainerId={campaignTextContainer} />
        <HowItWorks />
        {comboSection()}
        <Faq />
        <Reviews />
        <Examples />
        <EligibleBrands brands={brands || []} />
        <MostSubscribed />
        <div id={campaignTextContainer} />
      </>
    );
  };

  return (
    <div className={styles.root}>
      {centralHubTitle}
      {content()}
    </div>
  );
};
